import { Box, Grid, Typography } from '@mui/material'
import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import SelectTimeFrame from './SelectTimeView'
import { UseThreeSixtySavingDataCardProps } from '../hooks/use-three-sixty-saving-data-card-props'
import { DataCard } from 'src/Elements/DataCard'
import { SummaryTimeView } from '../types'
import {
  DASHBOARD_SUMMARY_DATA_CO2_CARD_ID,
  DASHBOARD_SUMMARY_DATA_DPP_CARD_ID,
  DASHBOARD_SUMMARY_DATA_ID,
  DASHBOARD_SUMMARY_DATA_NETWORK_CARD_ID,
  DASHBOARD_SUMMARY_DATA_PRODUCTION_CARD_ID,
} from 'src/Libs/qa/ids'
import { Paper } from '@e3dc-react/shell/Elements/Paper'

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  item: {
    flex: 1,
    minWidth: `calc(25% - ${theme.spacing(2)})`,
  },
  buttonGroup: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    display: 'flex',
    flexWrap: 'wrap',
  },
  subTitleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'center',
      flexDirection: 'row',
      marginTop: theme.spacing(1),
    },
  },
}))

//#region Summary Data Content

interface Props {
  threeSixtySavingCardProps: UseThreeSixtySavingDataCardProps
  co2WithUnit: string
  networkWithUnit: string
  productionWithUnit: string
  previousCo2WithUnit: string
  previousNetworkWithUnit: string
  previousProductionWithUnit: string
  isDppSupported?: boolean
  isSummaryDataLoading?: boolean
  paperClassName?: string
  handleSetPeriod: (newPeriod: SummaryTimeView) => void
}

const SummaryDataContent: React.FC<Props> = ({
  threeSixtySavingCardProps,
  co2WithUnit,
  networkWithUnit,
  productionWithUnit,
  previousCo2WithUnit,
  previousNetworkWithUnit,
  previousProductionWithUnit,
  isDppSupported = false,
  isSummaryDataLoading = false,
  paperClassName,
  handleSetPeriod,
}) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()

  return (
    <Paper contentClassName={classes.root} className={paperClassName} dataQa={DASHBOARD_SUMMARY_DATA_ID} title={t('dashboard.dataCards.title')}>
      <Box className={classes.buttonGroup}>
        <SelectTimeFrame setPeriod={handleSetPeriod} />
      </Box>
      <Grid container spacing={2} height="100%">
        {isDppSupported && (
          <Grid item xs={12}>
            <DataCard
              className={classes.item}
              headline={threeSixtySavingCardProps.headline}
              value={threeSixtySavingCardProps.value}
              subTitle={<Subtitle text={threeSixtySavingCardProps.subTitle.text} value={threeSixtySavingCardProps.subTitle.value} />}
              link={threeSixtySavingCardProps.link}
              dataQa={DASHBOARD_SUMMARY_DATA_DPP_CARD_ID}
              isLoading={threeSixtySavingCardProps.isLoading}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <DataCard
            className={classes.item}
            headline={t('dashboard.dataCards.production')}
            value={productionWithUnit}
            subTitle={<Subtitle value={previousProductionWithUnit} />}
            dataQa={DASHBOARD_SUMMARY_DATA_PRODUCTION_CARD_ID}
            isLoading={isSummaryDataLoading}
          />
        </Grid>
        <Grid item xs={12} flex={1}>
          <DataCard
            className={classes.item}
            headline={t('dashboard.dataCards.network')}
            value={networkWithUnit}
            subTitle={<Subtitle value={previousNetworkWithUnit} />}
            dataQa={DASHBOARD_SUMMARY_DATA_NETWORK_CARD_ID}
            isLoading={isSummaryDataLoading}
          />
        </Grid>
        <Grid item xs={12}>
          <DataCard
            className={classes.item}
            headline={t('dashboard.dataCards.co2')}
            value={co2WithUnit}
            subTitle={<Subtitle value={previousCo2WithUnit} />}
            dataQa={DASHBOARD_SUMMARY_DATA_CO2_CARD_ID}
            isLoading={isSummaryDataLoading}
          />
        </Grid>
      </Grid>
    </Paper>
  )
}

export default SummaryDataContent

//#endregion Summary Data Content

//#region Subtitle

interface SubtitleProps {
  value?: string
  text?: string
}

const Subtitle: React.FC<SubtitleProps> = ({ value, text }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()

  return (
    <Box className={classes.subTitleContainer}>
      <Typography sx={{ pl: 0.3 }} variant={'subtitle2'}>
        {text ?? t('dashboard.dataCards.sub-title-text')}
      </Typography>
      {value && (
        <Typography sx={{ pl: 0.3 }} variant={'subtitle2'}>
          {value}
        </Typography>
      )}
    </Box>
  )
}

//#endregion Subtitle
